import { Flex, useBreakpointValue } from '@chakra-ui/react';
import { startOfDay } from 'date-fns';
import { getTimezoneOffset, toDate } from 'date-fns-tz';
import { useResources } from 'datx-jsonapi-react';
import compact from 'lodash/compact';
import { Fragment, useEffect, useMemo } from 'react';

import { ScheduleCalendarHeader } from '@/components/ui/features/schedule/ScheduleCalendar/components/ScheduleCalendarHeader';
import {
  changeActiveDay,
  getEachDayOfInterval,
  getZonedDayInterval,
  getZonedWeekInterval,
} from '@/components/ui/features/schedule/ScheduleCalendar/ScheduleCalendar.utils';
import { ScheduleCalendarEmbedded } from '@/components/ui/features/schedule/ScheduleCalendar/ScheduleCalendarEmbedded';
import { ScheduleListEmbed } from '@/components/ui/features/schedule/ScheduleListView/ScheduleListEmbed';
import { ViewType } from '@/components/ui/features/schedule/static';
import { StringParam, useQueryParams } from '@/hooks/useQueryParams';
import { useUserEventTimezone } from '@/hooks/useUserEventTimezone';
import { querySortedThinEventSessions } from '@/queries/thin-event-sessions';
import { ThinEventSession } from '@/resources/ThinEventSession';

interface IScheduleTabsParams {
  view_type: ViewType;
  timeZone: string;
  eventId: string;
  day: string;
  view: string;
}

export const ScheduleSectionEmbed = () => {
  const isMobile = useBreakpointValue({ base: true, sm: false });
  const {
    view_type = ViewType.CALENDAR,
    timeZone,
    eventId,
    day,
    view = 'day',
    change,
  } = useQueryParams<IScheduleTabsParams>({
    view_type: StringParam,
    timeZone: StringParam,
    eventId: StringParam,
    day: StringParam,
    view: StringParam,
  });

  const adjustedTimezone = useUserEventTimezone(eventId);

  const { data: eventSessionIntervalStart } = useResources(querySortedThinEventSessions(eventId, 'starts_at,name'));
  const { data: eventSessionIntervalEnd } = useResources(querySortedThinEventSessions(eventId, '-ends_at'));

  const { startAt, endAt } = useMemo(() => {
    if (
      !day ||
      !eventSessionIntervalStart?.[0]?.startsAt ||
      !eventSessionIntervalEnd?.[0]?.endsAt ||
      !adjustedTimezone
    ) {
      return {
        startAt: null,
        endAt: null,
      };
    }

    const timezoneOffset = getTimezoneOffset(adjustedTimezone, toDate(day));
    const startOfActiveDay = startOfDay(toDate(day));
    const { startAtISO, endAtISO } =
      view === 'day'
        ? getZonedDayInterval(startOfActiveDay, timezoneOffset)
        : getZonedWeekInterval(startOfActiveDay, timezoneOffset);

    return {
      startAt: startAtISO,
      endAt: endAtISO,
    };
  }, [eventSessionIntervalStart, eventSessionIntervalEnd, day, view, adjustedTimezone]);

  const eventSessionInterval = useMemo(() => {
    return (
      eventSessionIntervalStart &&
      eventSessionIntervalEnd &&
      compact([...eventSessionIntervalStart, ...eventSessionIntervalEnd].map((item) => item as ThinEventSession))
    );
  }, [eventSessionIntervalStart, eventSessionIntervalEnd]);

  const days = useMemo(() => {
    if (!adjustedTimezone) {
      return [];
    }

    return getEachDayOfInterval(eventSessionInterval, adjustedTimezone);
  }, [eventSessionInterval, adjustedTimezone]);

  useEffect(() => changeActiveDay(change, days, day, timeZone, adjustedTimezone), [
    day,
    days,
    change,
    timeZone,
    adjustedTimezone,
  ]);

  return (
    <Flex flexDir="column" bgColor="white">
      <Flex justifyContent="center" w="full" py={{ base: 6, lg: 4 }} px={6}>
        <ScheduleCalendarHeader
          days={days}
          timeZone={adjustedTimezone}
          isDesktop={!isMobile}
          endEventDay={eventSessionIntervalEnd?.[0]?.endsAt}
          startEventDay={eventSessionIntervalStart?.[0]?.startsAt}
        />
      </Flex>
      {isMobile && <ScheduleListEmbed startAt={startAt} endAt={endAt} />}
      {!isMobile && (
        <Fragment>
          {view_type === ViewType.CALENDAR && <ScheduleCalendarEmbedded startAt={startAt} endAt={endAt} days={days} />}
          {view_type === ViewType.LIST && <ScheduleListEmbed startAt={startAt} endAt={endAt} />}
        </Fragment>
      )}
    </Flex>
  );
};
